import { Row } from "antd";
import { BulbOutlined, ExportOutlined } from "@ant-design/icons";
import { useState, useContext } from "react";
import styled from 'styled-components';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Box from "../../../components/utility/box";
import CreateTaskHeader from "../../ScrumBoard/Task/TaskCreateOrUpdate/CreateTaskHeader/CreateTaskHeader";
import { AppContext } from "../../../context/ContextProvider";

const SecurityContentHold = styled.div`
  .isoBoxWrapper {
    height: auto;
    border: none;
    margin: 0;
  }
`;

// Remediation guidance lives in a bounded "card" so it reads as a distinct,
// actionable block rather than just another label/value row.
const RecommendationCard = styled.div`
  margin-top: 22px;
  border: 1px solid #e3e8f4;
  background: #f8faff;
  border-radius: 10px;
  padding: 16px 18px;
`;

const RecommendationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-family: 'Poppins';
  font-weight: 600;
  font-size: 15px;
  color: #1f1f1f;

  .anticon {
    color: #52c41a;
    font-size: 16px;
  }
`;

// "Text" comes back as markdown (**bold**, `inline code`). Darker body color
// than the plain subtitles so the guidance is comfortably readable.
const RecommendationText = styled.div`
  font-size: 13px;
  color: #4a4a4a;
  font-family: 'Nunito Sans';
  font-weight: 600;
  line-height: 1.65;

  p { margin: 0 0 8px; }
  p:last-child { margin-bottom: 0; }
  strong { color: #1f1f1f; font-weight: 700; }
  code {
    background: #eef1f6;
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 12px;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    color: #1f1f1f;
  }
`;

const RecommendationLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #dfe5f1;
  width: 100%;
  font-family: 'Nunito Sans';
  font-weight: 600;
  font-size: 13px;
  color: #4D99EF;
  word-break: break-all;

  &:hover { color: #2f7fe0; text-decoration: underline; }
`;

// react-markdown renders inline code via the `code` component; keep it inline
// (the remediation text has no fenced code blocks, only short `inline` spans).
const markdownComponents = {
    code: ({ children, ...props }) => <code {...props}>{children}</code>,
};

let myTitle = (title) => (
    <div style={{display: "flex"}}>
        {/* <img src={TitleIcon} style={imgStyle} alt="image" /> */}
        <span>{title}</span>
    </div>
);

let mySubTitle = (subtitle) => (
    <div>
        <span> {subtitle} </span>
    </div>
);

let boxStyle = {
    border: '0',
    padding: "0",
    width: "100%",
    marginTop: "18px",
}

let titleStyle = {
    margin: '0 0 2px',
    fontSize: '16px',
    fontFamily: 'Poppins',
    fontWeight: '600',
    color: "#1f1f1f",

}

let subTitleStyle = {
    margin: '0',
    fontSize: '13px',
    color: "#a6a6a6",
    fontFamily: 'Nunito Sans',
    fontWeight: '600',
}

export default function SecurityDrawerData({ data }) {
    const [securityData, setSecurityData] = useState(data);
    const { openRightDrawer, setOpenRightDrawer } = useContext(AppContext);
    let regExp = /\[([^)]+)\]/;

    // Titles may or may not contain a bracketed "[check title]" segment.
    // Fall back to the full title so the drawer never crashes on plain titles.
    const title = securityData?.Title ?? "";
    const bracketMatch = regExp.exec(title);
    const titleSubtitle = title.includes("]") ? title.split("]")[1] : title;
    const checkTitle = bracketMatch ? bracketMatch[1] : title;

    // Per-report remediation guidance (markdown text + a reference URL).
    // Optional — older/other finding types may not include it.
    const recommendation = securityData?.Remediation?.Recommendation ?? null;
    const recommendationText = recommendation?.Text ?? "";
    const recommendationUrl = recommendation?.Url ?? "";

    return (
        <SecurityContentHold>

    <CreateTaskHeader
        data={securityData}
        buttonContent="Scan Outcome"
        // values={{editing: true}}
        onCancel={() => {
          setOpenRightDrawer({
            drawerType: false,
            drawerProps: {
              data: securityData
            },
          });
        }}
      />
        <div style={{padding: "20px"}}>
            <Box title="Title"
                subtitle={titleSubtitle}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={{
                    border: '0',
                    padding: "0",
                    width: "100%",
                    marginTop: "90px"
            }}
            >
            </Box>
            <Box title="Description"
                subtitle={securityData.Description}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Region"
                subtitle={securityData.Resources[0].Region}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Status"
                subtitle={securityData.Compliance.Status}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Check Title"
                subtitle={checkTitle}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            {(recommendationText || recommendationUrl) && (
                <RecommendationCard>
                    <RecommendationHeader>
                        <BulbOutlined />
                        <span>Recommendation</span>
                    </RecommendationHeader>
                    {recommendationText && (
                        <RecommendationText>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents}
                            >
                                {recommendationText}
                            </ReactMarkdown>
                        </RecommendationText>
                    )}
                    {recommendationUrl && (
                        <RecommendationLink
                            href={recommendationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <ExportOutlined />
                            {recommendationUrl}
                        </RecommendationLink>
                    )}
                </RecommendationCard>
            )}
            {/* <Box title="Resource ID"
                subtitle={securityData.Resources[0].Id}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Check ID"
                subtitle={securityData.Compliance.Status}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Status Extended"
                subtitle={securityData.Compliance.Status}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Risk"
                subtitle={securityData.Compliance.Status}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Recommendation"
                subtitle={securityData.Compliance.Status}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Recommendation URL"
                subtitle={securityData.Compliance.Status}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box>
            <Box title="Check Description"
                subtitle={securityData.Compliance.Status}
                titleStyle={titleStyle}
                subTitleStyle={subTitleStyle}
                style={boxStyle}
            >
            </Box> */}
            </div>
        </SecurityContentHold>
    );
}